/**
 * Fetches a workshop's real, BFF-generated Data Tables content (ADO #685/
 * #688) — the replacement for the static `WS4_DATA` fixture in
 * `data-tables-content.ts`.
 *
 * Generation happens server-side (see
 * `services/bff/app/data_tables_generation.py`) when a workshop's assets
 * change, and persists to `<workshopId>/generated/data-tables.json` on the
 * existing `workshops` blob tier (ADO #686) — this hook just reads that
 * blob via the same `getJson` helper every other JSON-on-blob workshop
 * record uses. No new backend endpoint was needed: the existing
 * `GET /api/docs/{tier}/file` route already serves it.
 *
 * Returns `data: null` (not an error) both while nothing has been generated
 * yet and while the workshop backend is IndexedDB-only (`VITE_WORKSHOP_BACKEND`
 * off) — callers should fall back to the static WS4 fixture only for WS4, and
 * render an explicit empty state otherwise (ADO #685: no silent fallback to
 * fiction for a real workshop with no data tables yet).
 */

import { useEffect, useState } from "react";
import { isWorkshopBffEnabled } from "@/env";
import { getJson } from "@/lib/docs-api";
import { useGeneratedWorkshopContent } from "@/stores/useGeneratedWorkshopContent";
import type { WorkshopDataTables } from "./data-tables-content";

const WORKSHOPS_TIER = "workshops";

/** Tier-relative blob path for a workshop's generated Data Tables — must
 *  match `generated_content_path(workshop_id, "data-tables")` in the BFF
 *  exactly. */
export function generatedDataTablesPath(workshopId: string): string {
  return `${workshopId}/generated/data-tables.json`;
}

export type GeneratedDataTablesState = {
  data: WorkshopDataTables | null;
  loading: boolean;
  error: boolean;
};

export function useGeneratedDataTables(workshopId: string): GeneratedDataTablesState {
  const [state, setState] = useState<GeneratedDataTablesState>({
    data: null,
    loading: isWorkshopBffEnabled(),
    error: false,
  });

  useEffect(() => {
    if (!isWorkshopBffEnabled()) {
      // Local/seed mode has no server-side generation to read — the caller
      // falls back to the static fixture (WS4) or the empty state (others).
      setState({ data: null, loading: false, error: false });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: false }));
    getJson<WorkshopDataTables>(WORKSHOPS_TIER, generatedDataTablesPath(workshopId))
      .then((value) => {
        // Write-through to the synchronous cache the chat's context-injection
        // path reads (ADO #685 wrap-up) — see useGeneratedWorkshopContent.
        useGeneratedWorkshopContent.getState().setDataTables(workshopId, value);
        if (!cancelled) setState({ data: value, loading: false, error: false });
      })
      .catch(() => {
        // A transient fetch failure isn't "no data tables" — surface it
        // distinctly so the modal doesn't quietly show an empty state for a
        // real error.
        if (!cancelled) setState({ data: null, loading: false, error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  return state;
}
